<template>
  <div class="flex flex-col justify-center py-12 sm:px-6 lg:px-8">

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">

      <div class="h-7 text-center">
        <div v-if="authToken" class="inline-flex text-base leading-6 font-medium rounded-md text-green-600 cursor-not-allowed">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
            {{ verificationMessage }}
        </div>
      </div>

      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <img class="w-auto h-20 mx-auto mt-1" src="@/assets/hp/kairo-logo-v2.png" alt="Kairo">
        <h2
          class="text-gray-700 font-medium text-center mb-6"
        >Log in to Kairo</h2>

        <button
          type="button"
          :disabled="lockButtons"
          @click="loginWithGoogle"
          class="disabled:opacity-50 w-full inline-flex justify-center items-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg class="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.3-1.7 3.9-5.4 3.9-3.2 0-5.9-2.7-5.9-6s2.7-6 5.9-6c1.8 0 3 .8 3.7 1.4l2.5-2.4C16.6 3.4 14.5 2.5 12 2.5 6.9 2.5 2.8 6.6 2.8 11.7S6.9 20.9 12 20.9c6.9 0 8.6-4.9 8.6-7.4 0-.5-.1-.9-.1-1.3H12z"/>
            <path fill="#4285F4" d="M3.8 7.2l3.2 2.3c.9-1.8 2.8-3 5-3 1.8 0 3 .8 3.7 1.4l2.5-2.4C16.6 3.4 14.5 2.5 12 2.5c-3.6 0-6.8 2.1-8.2 4.7z"/>
            <path fill="#FBBC05" d="M12 20.9c2.4 0 4.4-.8 5.8-2.2l-2.8-2.2c-.8.5-1.8.9-3 .9-2.2 0-4.1-1.4-4.8-3.4l-3.3 2.5c1.4 2.8 4.5 4.4 8.1 4.4z"/>
            <path fill="#34A853" d="M3.8 16.6l3.3-2.5c-.2-.6-.3-1.3-.3-2s.1-1.3.3-2L3.8 7.6C3.2 8.8 2.8 10.2 2.8 11.7s.4 2.9 1 4.9z"/>
          </svg>
          Continue with Google
        </button>

        <div class="relative my-6">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-300"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="bg-white px-2 text-gray-500">or continue with email</span>
          </div>
        </div>

        <div v-if="msgError" class="rounded-md bg-red-50 p-4 mb-6">
          <div class="flex">
            <div class="flex-shrink-0">
              <!-- Heroicon name: x-circle -->
              <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">
                There was a problem!
              </h3>
              <div class="mt-2 text-sm text-red-700">
                {{msgError}}
              </div>
            </div>
          </div>
        </div>
        <div v-if="msgError" class="rounded-md bg-amber-50 border border-amber-200 p-4 mb-6">
          <p class="text-sm font-medium text-amber-900">
            If you created your Kairo account before 15 April 2026, you may still need to sign in to the previous Kairo app.
          </p>
          <p class="mt-2 text-sm text-amber-800">
            You can log in there at
            <a
              href="https://app.getkairo.com"
              class="font-medium underline decoration-amber-400 underline-offset-2 hover:text-amber-900"
            >app.getkairo.com</a>.
          </p>
        </div>
        <div v-if="msgSuccess" class="rounded-md bg-green-50 p-4 mb-6">
          <div class="flex">
            <div class="flex-shrink-0">
              <!-- Heroicon name: x-circle -->
              <svg class="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">
                Please check your email!
              </h3>
              <div class="mt-2 text-sm text-green-700">
                {{msgSuccess}}
              </div>
            </div>
          </div>
        </div>

        <div>
          <Form
            @submit="login()"
            class="space-y-6"
          >
            <div>
              <label for="email_login" class="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div class="mt-1">
                <Field
                  name="email"
                  :rules="validateEmail"
                  id="email_login"
                  v-model="crendentials.email"
                  type="email"
                  placeholder="hey@email.com"
                  autocomplete="email"
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <ErrorMessage name="email" class="mt-2 text-sm text-red-600" />

              </div>
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div class="mt-1">
                <Field
                  v-model="crendentials.password"
                  :type="passwordType"
                  id="password"
                  name="password"
                  :rules="isRequired"
                  autocomplete="current-password"
                  class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                <ErrorMessage name="password" class="mt-2 text-sm text-red-600" />
              </div>
            </div>

            <div>
              <button
                :disabled="lockButtons"
                class="disabled:opacity-50 w-full border-green-600 border-2 border-b-3 items-center p-2 pb-3 rounded-lg shadow-sm text-green-600 bg-green-200 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-50"
                style="box-shadow: inset 0px -6px 0px #34D399, inset 0 -7px 0 0 #ECFDF5, inset 0px 1px 0px 1px #ECFDF5;"
              >
                Sign in
              </button>
            </div>

            <div class="relative">
            </div>
          </Form>
        </div>

        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
            <span class="px-2 bg-white text-gray-500">
            </span>
            </div>
          </div>
          <div>
            <div class="relative flex justify-center text-sm mt-4">
              <span class="px-2 bg-white text-gray-500">
                <router-link
                  to="/recover"
                  class="font-medium text-indigo-600 hover:text-indigo-500"
                >Can't log in?
                </router-link>
                •
                <router-link
                  to="/signup"
                  class="font-medium text-indigo-600 hover:text-indigo-500"
                >Sign up for an account
                </router-link>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <AppFooter />

</template>

<script>
  import { mapActions, mapGetters } from "vuex";
  import { Field, Form, ErrorMessage } from 'vee-validate';
  import AppFooter from "../components/AppFooter.vue";
  import { resolveAuthRedirect } from '../helpers/login-redirect';

  const EMAIL_CONFIRMATION_STATUS_KEY = 'kairo_email_confirmation_status';

  function readEmailConfirmationStatus() {
    try {
      const raw = window.sessionStorage.getItem(EMAIL_CONFIRMATION_STATUS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error('Unable to read email confirmation status.', error);
      return null;
    }
  }

  export default {
    components: {
      Field,
      Form,
      ErrorMessage,
      AppFooter
    },
    data() {
      return {
        crendentials: {
          password: "",
          email: ""
        },
        hidePassword: true,
        toggleModal: false,
        lockButtons: false,
        msgError: null,
        msgSuccess: null,
        confirmationToken: null,
        verificationMessage: 'Verifying...',
      };
    },
    computed: {
      ...mapGetters("auth", ["authToken"]),
      passwordType() {
        return this.hidePassword ? "password" : "text";
      },
      passwordIcon() {
        return this.hidePassword ? "eye-open" : "eye-closed";
      },
    },
    methods: {
      ...mapActions("auth", [
        "attemptLogin",
        "attemptExternalLogin",
        "completeExternalLogin",
      ]),
      async loginWithGoogle() {
        this.msgError = '';
        this.msgSuccess = '';
        this.lockButtons = true;

        try {
          await this.attemptExternalLogin({
            provider: 'google',
            redirectTo: window.location.href,
          });
        } catch (error) {
          this.msgError = `${error}`;
          this.lockButtons = false;
        }
      },
      applyEmailConfirmationStatus(status) {
        if (!status?.status) {
          return;
        }

        if (status.status === 'verifying') {
          this.msgError = null;
          this.msgSuccess = null;
          this.verificationMessage = status.message || 'Verifying your email...';
          return;
        }

        this.verificationMessage = 'Verifying...';

        if (status.status === 'success') {
          this.msgError = null;
          this.msgSuccess = status.message || 'Your email has been verified. Please sign in.';
          return;
        }

        if (status.status === 'error') {
          this.msgSuccess = null;
          this.msgError = status.message || 'Unable to verify your email right now.';
        }
      },
      handleEmailConfirmationEvent(event) {
        this.applyEmailConfirmationStatus(event.detail);
      },
      async completeExternalLoginFromCallback() {
        const hasOAuthCode = new URLSearchParams(window.location.search).has('code');
        const hash = window.location.hash || '';
        const hasOAuthHash = ['access_token=', 'refresh_token=', 'error=', 'error_code=', 'error_description=']
          .some(fragment => hash.includes(fragment));

        if (!hasOAuthCode && !hasOAuthHash) {
          return;
        }

        this.lockButtons = true;
        this.msgError = '';

        try {
          const currentUser = await this.completeExternalLogin();
          this.$router.replace(resolveAuthRedirect(this.$route.query, currentUser));
        } catch (error) {
          this.msgError = `${error}`;
          this.lockButtons = false;
        }
      },
      login() {
        this.msgError = '';
        this.msgSuccess = '';
        this.lockButtons = true;
        this.attemptLogin({ ...this.crendentials })
            .then((currentUser) => {
              this.lockButtons = false;
              this.$router.push(resolveAuthRedirect(this.$route.query, currentUser));
            })
            .catch(error => {
              let msgError = `${error}`;
              if (msgError.indexOf("invalid_grant")) {
                msgError = 'There isn\'t an account for this email, or password is invalid.';
              }
              this.msgError = msgError;
              this.lockButtons = false;
            });
      },
      isRequired(value) {
        return value ? true : 'This field is required';
      },
      validateEmail(value) {
        if (!value) {
          return 'This field is required';
        }
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value)) {
          return 'This field must be a valid email';
        }
        return true;
      },
    },
    mounted() {
      window.addEventListener('kairo:email-confirmation', this.handleEmailConfirmationEvent);
      this.applyEmailConfirmationStatus(readEmailConfirmationStatus());
      this.completeExternalLoginFromCallback();

      try {
        this.confirmationToken = decodeURIComponent(document.location.hash).split(
            "confirmation_token="
        )[1];
      } catch {
        this.confirmationToken = null;
      }
    },
    beforeUnmount() {
      window.removeEventListener('kairo:email-confirmation', this.handleEmailConfirmationEvent);
    }
  };
</script>
